/**
 * 功能备注
 * - 统一封装图片/视频上传至 CDN 的流程
 * - 自动判断类型（image/video），并依据类型切换上传路径：
 *   - 图片：/api/image/upload（大小限制 ≤10MB）
 *   - 视频：/api/video/upload（大小限制 ≤50MB）
 * - 使用 getUploadDomain() 生成上传域名，兼容返回的相对/绝对路径
 * - 返回 Promise，成功时返回 { url, isVideo }；失败时返回带 code 的错误
 *   - 错误码：FILE_TOO_LARGE / NO_URL / UPLOAD_ERROR
 *
 * 使用示例
 * import { uploadMedia } from '@/utils/uploadService.js'
 * 
 * // 在组件中：
 * uploadMedia(file, { space: 'cs', isReduce: true })
 *   .then(({ url, isVideo }) => {
 *     // TODO: 使用 url（例如写入文件项或消息）
 *   })
 *   .catch(err => {
 *     if (err.code === 'FILE_TOO_LARGE') {
 *       // TODO: 按 isVideo 显示不同大小提示
 *     } else {
 *       // TODO: 统一上传失败提示
 *     }
 *   })
 */
import $ from 'jquery'
import { getUploadDomain } from '@/utils/index.js'

/**
 * 上传图片/视频到 CDN
 * @param {File|{raw:File,name?:string}} file - 选择的文件或包含 raw 的对象
 * @param {{space?:string,isReduce?:boolean}} options - 上传可选参数
 * @returns {Promise<{url:string,isVideo:boolean}>} - 上传结果
 */
export function uploadMedia(file, options = {}) {
  const raw = file && file.raw ? file.raw : file
  const filename = (raw && raw.name) || (file && file.name) || 'upload.bin'
  const typeStr = String((raw && raw.type) || '').toLowerCase()
  const nameStr = String(filename || '').toLowerCase()
  const isVideo = typeStr.startsWith('video/') || /\.(mp4|webm|ogg|mov|m4v)$/i.test(nameStr)
  const sizeMB = raw && raw.size ? raw.size / 1024 / 1024 : 0
  const maxMB = isVideo ? 50 : 10
  if (sizeMB > maxMB) {
    const err = new Error('FILE_TOO_LARGE')
    err.code = 'FILE_TOO_LARGE'
    err.isVideo = isVideo
    err.sizeMB = sizeMB
    err.maxMB = maxMB
    return Promise.reject(err)
  }
  const domain = process.env.VUE_APP_UPLOAD_URL;
  const path = isVideo ? '/api/video/upload' : '/api/image/upload'
  const form = new FormData()
  form.append(isVideo ? 'videoFile' : 'imageFile', raw, filename)
  if (options.space) form.append('space', options.space)
  if (options.isReduce !== undefined) form.append('isReduce', String(options.isReduce))
  return new Promise((resolve, reject) => {
    $.ajax({
      method: 'POST',
      url: `${domain}${path}`,
      data: form,
      processData: false,
      contentType: false,
      dataType: 'json',
      success: (ret) => {
        let url =
          (ret && ret.data && (ret.data.imageUrl || ret.data.videoUrl || ret.data.url)) ||
          (ret && (ret.imageUrl || ret.videoUrl || ret.url)) ||
          ''
        if (url && !/^https?:\/\//i.test(url)) {
          url = `${domain}${url.startsWith('/') ? url : `/${url}`}`
        }
        if (!url) {
          const e = new Error('NO_URL')
          e.code = 'NO_URL'
          reject(e)
          return
        }
        resolve({ url, isVideo })
      },
      error: (xhr, status, err) => {
        const e = err || new Error(status || 'UPLOAD_ERROR')
        e.code = 'UPLOAD_ERROR'
        reject(e)
      }
    })
  })
}
